import { isEmpty } from '@ember/utils'
import type { NormalizedInterval } from 'date-fns'
import type { StatsScope } from 'district-ui-client/utils/student-events/stats-scope'
import type StudentEventsService from 'district-ui-client/services/student-events'
import fillMonths from 'district-ui-client/utils/student-events/fill-months'

export interface BooksReadByLevelTotalsResponse {
  books_read_by_level_totals_summary_by_grade: SummaryData[]
  books_read_by_level_totals_by_grade_by_month: MonthData[]
}

interface SummaryData {
  grade_position: number
  data: [
    { category: 'total_count'; count: number },
    { category: 'student_count'; count: number },
    { category: 'student_average'; average: number },
  ]
}

interface MonthData {
  grade_position: number
  months: {
    month: string
    '0-300'?: number
    '300-500'?: number
    '500-800'?: number
    '800-2000'?: number
  }[]
}

export interface BooksReadByLevelTotalsData {
  grade_position: number
  summary: {
    total_count: number
    student_count: number
    student_average: number
  }
  months: {
    month: string
    '0-300': number
    '300-500': number
    '500-800': number
    '800-2000': number
  }[]
}

export async function booksReadByLevelTotals(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: StatsScope,
  period: NormalizedInterval,
): Promise<Nullable<BooksReadByLevelTotalsData[]>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('books_read_by_level_totals_by_grade_by_month', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<BooksReadByLevelTotalsResponse>(url, query)
  if (!data) return null

  const {
    books_read_by_level_totals_summary_by_grade: summaryData,
    books_read_by_level_totals_by_grade_by_month: monthData,
  } = data
  if (summaryData.length === 0) {
    return null
  }

  return groupByGrade(summaryData, monthData, period)
}

function groupByGrade(summaryData: SummaryData[], monthData: MonthData[], period: NormalizedInterval) {
  const sortedSummaryData = [...summaryData].sort((a, b) => a.grade_position - b.grade_position)

  const defaultMonthData = {
    '0-300': 0,
    '300-500': 0,
    '500-800': 0,
    '800-2000': 0,
  }

  return sortedSummaryData
    .map((summary) => {
      const gradeMonthData = monthData.find((month) => month.grade_position === summary.grade_position)
      if (!gradeMonthData) return null

      const student_count = summary.data.find((value) => value.category === 'student_count')?.count ?? 0
      const total_count = summary.data.find((value) => value.category === 'total_count')?.count ?? 0
      const student_average = summary.data.find((value) => value.category === 'student_average')?.average ?? 0

      const parsedMonthData = gradeMonthData.months.map((month) => ({
        ...defaultMonthData,
        ...month,
      }))
      const fullMonthData = fillMonths(parsedMonthData, period, {
        '0-300': 0,
        '300-500': 0,
        '500-800': 0,
        '800-2000': 0,
      })

      return {
        grade_position: summary.grade_position,
        summary: {
          total_count,
          student_count,
          student_average,
        },
        months: fullMonthData,
      }
    })
    .filter((data) => data !== null)
}
